import { Component, DoCheck, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ScheduledEvent } from '../../../../../../../../../../../models/scheduled-event';
import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601, getISO8601DateStringFromMoment,
  propExists,
} from '../../../../../../../../../../../utils';
import { CustomerSearchResult } from '../../../../../../../../../../../models/customer';
import { ListResponseCountOptional } from '../../../../../../../../../../../models/list-response';
import { CustomerService } from '../../../../../../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../../../../../../services/organization-config/organization-config.service';
import { AccountService } from '../../../../../../../../../../../services/account/account.service';
import { ScheduledEventService } from '../../../../../../../../../../../services/scheduled-event/scheduled-event.service';
import { openClimberAppInNewTabAsCustomer, openClimberAppInNewTabAsStaff } from '../../../../../../../../../../../utils-models';
import { EventType } from '../../../../../../../../../../../models/event-type';
import { LocationService } from '../../../../../../../../../../../services/location/location.service';
import { CapitanLocation } from '../../../../../../../../../../../models/location';
import { Event } from '../../../../../../../../../../../models/event';
import { EventService } from '../../../../../../../../../../../services/event/event.service';
import { EventTypeService } from '../../../../../../../../../../../services/event-type/event-type.service';
import { AgeRestrictionBypass, eventAgeAsOfDate, eventTypeAgeAsOfDate } from '../../../../../../../../../../../models/age-restriction';
import * as moment from 'moment';


@Component({
  selector: 'app-add-to-event-waitlist-modal',
  templateUrl: './add-to-event-waitlist-modal.component.html',
  styleUrls: ['./add-to-event-waitlist-modal.component.css']
})
export class AddToEventWaitlistModalComponent implements OnInit, DoCheck {

  @Input() eventType: EventType | null = null;
  @Input() scheduledEvent: ScheduledEvent | null = null;
  @Input() addedToWaitlistCallback: (result: CallbackResult) => undefined;

  public addViaClimberApp: boolean | null = null;

  public selectedCustomer: CustomerSearchResult | null = null;
  public notificationEmail = '';

  public prefersMonday = false;
  public prefersTuesday = false;
  public prefersWednesday = false;
  public prefersThursday = false;
  public prefersFriday = false;
  public prefersSaturday = false;
  public prefersSunday = false;
  public prefersLocation: {[key: number]: boolean} = {};

  public locations: CapitanLocation[] = null;

  // Only used if this is an event's waitlist, for the age restriction check.
  public event: Event = null;
  public eventTypeOfEvent: EventType = null;
  public loadingEvent = false;
  public loadingEventTypeOfEvent = false;

  public readonly ageRestriction = new AgeRestrictionBypass();

  public loadingSubmit = false;
  public loadingCustomers = false;

  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: ListResponseCountOptional<CustomerSearchResult>;

  public errorMessage = '';
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    public activeModal: NgbActiveModal,
    public locationService: LocationService,
    private customerService: CustomerService,
    private orgService: OrganizationConfigService,
    private accountService: AccountService,
    private scheduledEventService: ScheduledEventService,
    private eventService: EventService,
    private eventTypeService: EventTypeService,
  ) {}

  ngOnInit() {
    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });

    if (!this.scheduledEvent) {
      this.addViaClimberApp = false;
    }

    this.loadLocations();

    if (this.scheduledEvent) {
      this.loadEvent();
      this.loadEventTypeOfEvent();
    }
  }

  ngDoCheck() {
    const participants = ((this.addViaClimberApp === false) && (this.selectedCustomer !== null)) ? [{
      key: this.selectedCustomer.id.toString(),
      name: this.selectedCustomer.first_name + ' ' + this.selectedCustomer.last_name,
      birthday: this.selectedCustomer.birthday,
    }] : [];

    if (!this.scheduledEvent && this.eventType) {
      const orgConfig = this.orgService.getOrganizationConfig();
      this.ageRestriction.refresh(
        'event-type-' + this.eventType.id.toString(),
        participants,
        this.eventType,
        eventTypeAgeAsOfDate(orgConfig ? orgConfig.time_zone : null)
      );
      return;
    }

    if ((this.event === null) || (this.eventTypeOfEvent === null)) {
      this.ageRestriction.refresh('', [], null);
      return;
    }

    this.ageRestriction.refresh(
      'event-' + this.event.id.toString(),
      participants,
      this.eventTypeOfEvent,
      eventAgeAsOfDate(this.event)
    );
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      this.customerList.results &&
      (this.customerList.results.length > 0)
    );
  }

  public selectCustomer(customer: CustomerSearchResult) {
    this.selectedCustomer = customer;
    this.search = (
      customer.first_name + ' ' + customer.last_name
    );
    this.notificationEmail = customer.email;
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    this.loadingCustomers = true;
    this.selectedCustomer = null;

    const orgConfig = this.orgService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  public handleGoToScheduler() {
    this.loadingSubmit = true;
    this.errorMessage = '';

    if (this.selectedCustomer === null) {
      this.customerService
        .getStaffClimberAppToken()
        .then(token => {
          openClimberAppInNewTabAsStaff(
            token,
            this.orgService.getOrganizationConfig().climber_app_base_url,
            (
              this.scheduledEvent.event_waitlist_url
              + '?use_saved_response=false'
            )
          );
          this.activeModal.close();
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to add to this waitlist.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    } else {
      this.customerService
        .getCustomerClimberAppToken(this.selectedCustomer.id)
        .then(token => {
          openClimberAppInNewTabAsCustomer(
            token,
            this.selectedCustomer.id,
            this.orgService.getOrganizationConfig().climber_app_base_url,
            (
              this.scheduledEvent.event_waitlist_url
              + '?use_saved_response=false'
            )
          );
          this.activeModal.close();
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to add to this waitlist.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    }
  }

  public addToWaitlistAllowed(): boolean {
    return (
      (this.selectedCustomer !== null) &&
      !this.loadingEvent &&
      !this.loadingEventTypeOfEvent &&
      !this.ageRestriction.blocksSend()
    );
  }

  public handleAddToWaitlist() {
    if (!this.selectedCustomer) { return; }

    this.loadingSubmit = true;
    this.errorMessage = '';

    let addPromise: Promise<void>;

    if (this.scheduledEvent) {
      addPromise = this.scheduledEventService
        .addCustomerToEventWaitlist(
          this.scheduledEvent.event_id,
          this.selectedCustomer.id,
          this.notificationEmail,
          this.ageRestriction.codesFor(this.selectedCustomer.id.toString())
        );
    } else {
      const preferredDaysStringList: string[] = [];

      if (this.prefersMonday) {
        preferredDaysStringList.push('M');
      }
      if (this.prefersTuesday) {
        preferredDaysStringList.push('Tu');
      }
      if (this.prefersWednesday) {
        preferredDaysStringList.push('W');
      }
      if (this.prefersThursday) {
        preferredDaysStringList.push('Th');
      }
      if (this.prefersFriday) {
        preferredDaysStringList.push('F');
      }
      if (this.prefersSaturday) {
        preferredDaysStringList.push('Sa');
      }
      if (this.prefersSunday) {
        preferredDaysStringList.push('Su');
      }

      const preferredLocationIdsStringList: string[] = [];

      for (const loc of this.locations) {
        if (this.prefersLocation[loc.id]) {
          preferredLocationIdsStringList.push(loc.id.toString());
        }
      }

      addPromise = this.scheduledEventService
        .addCustomerToEventTypeWaitlist(
          this.eventType.id,
          this.selectedCustomer.id,
          this.notificationEmail,
          preferredDaysStringList.join(' '),
          preferredLocationIdsStringList.join(','),
          this.ageRestriction.codesFor(this.selectedCustomer.id.toString())
        );
    }

    addPromise
      .then(() => {
        this.addedToWaitlistCallback({
          text: 'Customer added to waitlist successfully.',
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to add to this waitlist.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingSubmit = false;
      });
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  private loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

  private loadEvent() {
    this.loadingEvent = true;

    this.eventService
      .getEvent(this.scheduledEvent.event_id)
      .then(event => { this.event = event; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingEvent = false; });
  }

  private loadEventTypeOfEvent() {
    this.loadingEventTypeOfEvent = true;

    this.eventTypeService
      .getEventType(this.scheduledEvent.event_type_id)
      .then(eventType => { this.eventTypeOfEvent = eventType; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingEventTypeOfEvent = false; });
  }

  private loadLocations() {
    this.locations = this.locationService.getLocations();
    for (const loc of this.locations) {
      this.prefersLocation[loc.id] = false;
    }
    if (this.locations === null) {
      this.locationService
        .loadLocations()
        .then(() => {
          this.locations = this.locationService.getLocations();
          for (const loc of this.locations) {
            this.prefersLocation[loc.id] = false;
          }
        });
    }
  }

}
